import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import CustomImageComponent from '@/components/shared/custom/custom-image';
import AirPlaneIcon from '@/components/shared/icons/airplane-out-icon';
import HomeIcon from '@/components/shared/icons/home-icon';
import { Rutas } from '@/constants/rutas';
import { translate } from '@/data/fixture-transalations';
import { getDate_dd_MM } from '@/helpers/datetime-helper';
import { getCompetitionImage } from '@/helpers/leagues-helper';
import { getCssClass } from '@/helpers/fixtures-helper';
import Link from 'next/link';
import CustomTeamImageWithDefault from '../shared/custom/CustomTeamImageWithDefault';
import CustomSectionWrapper from '@/DesignSystem/wrappers/CustomSectionWrapper';

export const SingleFixtureNextLast = ({
    fixture,
    teamId,
    isLastFixtures = false,
    isLastInList = false,
    width = '20%',
}) => {
    const theme = useTheme();
    const cssClass = getCssClass(fixture);

    const rival = fixture?.h?.id === teamId ? fixture?.a : fixture?.h;
    const rivalId = rival?.id;
    const rivalName = rival?.nm;

    const idPartido = fixture?.id;
    const fechaPartido = fixture?.dt;

    const homeGoals = fixture?.h?.g;
    const awayGoals = fixture?.a?.g;

    const icon = fixture?.h?.id === teamId ? <HomeIcon width={15} /> : <AirPlaneIcon width={15} />;

    return (
        <Link
            href={`${Rutas.PARTIDO}/${idPartido}/alineaciones-probables`}
            prefetch={false}
            width={'100%'}
            style={{
                cursor: 'pointer',
                borderRight: !isLastInList ? `1px solid ${theme.palette.divider}` : 'none',
                textDecoration: 'none',
                width: width,
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '2px',
            }}
        >
            <Box display={'flex'} alignItems={'center'} justifyContent={'center'} gap="4px">
                {icon}
                <Typography style={{ fontSize: '13px' }}>{getDate_dd_MM(fechaPartido)}</Typography>
            </Box>
            <Box
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: '8px',
                    paddingLeft: '6px',
                    paddingRight: '6px',
                }}
                variant="div"
            >
                <CustomTeamImageWithDefault
                    teamId={rivalId}
                    width={25}
                    height={25}
                    alt={rivalName}
                ></CustomTeamImageWithDefault>
                {isLastFixtures && (
                    <Box
                        className={cssClass}
                        style={{ display: 'flex', padding: '0 4px', borderRadius: '4px' }}
                        color={theme.palette.text.primary}
                    >
                        <span style={{ fontSize: '12px' }}>{homeGoals}</span>
                        <span style={{ fontSize: '12px' }}>-</span>
                        <span style={{ fontSize: '12px' }}>{awayGoals}</span>
                    </Box>
                )}
            </Box>
            <Box style={{ display: 'flex', marginTop: '4px', alignItems: 'center' }}>
                <CustomImageComponent
                    src={getCompetitionImage(fixture?.l, 15)}
                    width={15}
                    height={15}
                    alt="competición"
                    title="competición"
                    style={{ marginRight: '4px' }}
                />
                <Typography style={{ fontSize: '14px' }}>{translate(fixture?.rd, true)}</Typography>
            </Box>
        </Link>
    );
};

const FixturesNextLastSingleList = ({ fixtures, title, teamId, isLastFixtures }) => {
    const theme = useTheme();
    const accentColor = isLastFixtures
        ? theme.palette.border?.accent || '#69b59f'
        : theme.palette.border?.accent2 || '#bb86fc';

    if (fixtures?.length === 0) {
        return null;
    }

    const getAlphaColor = (color) => {
        try {
            if (color.startsWith('rgb(')) {
                return color.replace('rgb(', 'rgba(').replace(')', ', 0.25)');
            }
            return alpha(color, 0.25);
        } catch (error) {
            if (color.startsWith('#')) {
                return `${color}40`;
            }
            return color;
        }
    };

    return (
        <CustomSectionWrapper title={title}>
            <Box
                className="last-fixtures__body"
                sx={{
                    backgroundColor: theme.palette.background.default,
                    width: '100%',
                    height: '88px',
                }}
            >
                {fixtures?.map((fixture, index) => (
                    <SingleFixtureNextLast
                        fixture={fixture}
                        teamId={teamId}
                        key={fixture?.id ?? index}
                        isLastFixtures={isLastFixtures}
                        isLastInList={index === fixtures.length - 1}
                        width={`${100 / fixtures.length}%`}
                    />
                ))}
            </Box>
        </CustomSectionWrapper>
    );
};

const FixturesNextLast = ({ player, teamId }) => {
    const last = player.last;
    const next = player.next;

    return (
        <>
            <Box display={'flex'} flexDirection={{ xs: 'column', md: 'row' }} gap={1} width={'100%'} my={2}>
                <FixturesNextLastSingleList
                    fixtures={last}
                    title="Últimos partidos"
                    teamId={teamId}
                    isLastFixtures={true}
                />
            </Box>
            <Box display={'flex'} flexDirection={{ xs: 'column', md: 'row' }} gap={1} width={'100%'} mb={2}>
                <FixturesNextLastSingleList fixtures={next} title="Próximos partidos" teamId={teamId} />
            </Box>
        </>
    );
};

export default FixturesNextLast;
